import ResetPasswordForm from './components/resetPasswordForm';
import { resendVerificationSignup } from '~/services/authManagement';
import { useContext, useEffect, useState } from 'react';
import { CurrentUserContext } from '~/components/CurrentUserContext';
import { useLowDataMode } from '~/components/LowDataModeContext';
import { Stack, Text, Alert, Button, Card, Badge, Group, Loader, Switch } from '@mantine/core';
import { PageHeader } from '~/components/PageHeader';
import { notifications } from '@mantine/notifications';
import Settings from '~/defaultSettings';
import { UpgradeScopeSetting } from '~/components/UpgradeScope';
import { UserContributions } from './components/UserContributions';
import { getUserContributionBreakdown } from '~/services/filmEditHistory';
import { clearCache } from '~/services/film';
import { redirect } from 'react-router';
import { getUserProfile } from '~/services/users';
import type { User } from 'backend/client';
import type { Route } from './+types/profile';

const ProfilePage: React.FC<Route.ComponentProps> = ({ loaderData }) => {
  const { access, currentUser } = useContext(CurrentUserContext);
  const { isVerified } = access;
  const { profileUser } = loaderData;
  const isOwnProfile = currentUser?.id === profileUser.id;

  const [contributionBreakdown, setContributionBreakdown] = useState<{
    filmsCreated: number;
    edits: number;
  } | null>(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [savingLowDataMode, setSavingLowDataMode] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const { lowDataMode, setLowDataMode } = useLowDataMode();

  useEffect(() => {
    const loadBreakdown = async () => {
      if (profileUser.id && (profileUser.is_verified || isOwnProfile)) {
        setLoadingBreakdown(true);
        try {
          const breakdown = await getUserContributionBreakdown(profileUser.id);
          setContributionBreakdown(breakdown);
        } catch (error) {
          console.error('Error loading contribution breakdown:', error);
        } finally {
          setLoadingBreakdown(false);
        }
      }
    };
    loadBreakdown();
  }, [profileUser.id, profileUser.is_verified, isOwnProfile]);

  const getAccessLevel = () => {
    if (profileUser.is_admin) return 'Admin';
    else if (profileUser.is_editor) return 'Editor';
    else return 'Member';
  };

  const onResendVerificationEmail = async () => {
    if (!currentUser?.email) return;
    try {
      const result = await resendVerificationSignup({ email: currentUser.email });
      notifications.show({
        title: 'Success',
        message: `A new verification email has been sent to ${result.email}`,
        color: 'green',
      });
    } catch (e: any) {
      console.error(e);
      notifications.show({
        title: 'Error',
        message: e.message,
        color: 'red',
      });
    }
  };

  return (
    <div>
      <PageHeader size="h3" mb="lg">
        {isOwnProfile ? 'My Profile' : `${profileUser.username}'s Profile`}
      </PageHeader>

      {/* Verification prompt first — unverified users are routed here to verify */}
      {isOwnProfile && !isVerified() && (
        <Stack gap="md" mb="lg" style={{ maxWidth: 1000 }}>
          <Alert title="Please verify your email" color="yellow" variant="light">
            To get full access including comment, contribute, or reset your password, please verify
            your email.
          </Alert>

          <Button onClick={onResendVerificationEmail} mx="auto">
            Resend verification email
          </Button>
        </Stack>
      )}

      <Card style={{ maxWidth: 1000 }}>
        <Stack gap="md">
          <div>
            <Text fw={500} size="sm" c="dimmed">
              Username
            </Text>
            <Text>{profileUser.username}</Text>
          </div>

          {/* Only show email, password reset for own profile */}
          {isOwnProfile && currentUser?.email && (
            <>
              <div>
                <Text fw={500} size="sm" c="dimmed">
                  Email
                </Text>
                <Text>{currentUser.email}</Text>
              </div>
              {isVerified() && (
                <div>
                  <Text fw={500} size="sm" c="dimmed">
                    Password
                  </Text>
                  <ResetPasswordForm currentUser={currentUser} />
                </div>
              )}
            </>
          )}

          {/* Show access level for all */}
          <div>
            <Text fw={500} size="sm" c="dimmed">
              Access Level
            </Text>
            <Text>{getAccessLevel()}</Text>
          </div>

          {/* Settings - only for own profile */}
          {isOwnProfile && (
            <div>
              <Text fw={500} size="sm" c="dimmed">
                Settings
              </Text>
              <Group gap="xs" mt="xs" align="center">
                <Switch
                  label="Low Data Mode"
                  description="Lowers the resolution of images across the site to save data bandwidth."
                  checked={lowDataMode}
                  disabled={savingLowDataMode}
                  onChange={async (e) => {
                    const value = e.currentTarget.checked;
                    setSavingLowDataMode(true);
                    try {
                      await setLowDataMode(value);
                    } catch (_error) {
                      notifications.show({
                        title: 'Error',
                        message: 'Failed to update setting',
                        color: 'red',
                      });
                    } finally {
                      setSavingLowDataMode(false);
                    }
                  }}
                />
              </Group>
              <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>
                <UpgradeScopeSetting />
              </div>
            </div>
          )}

          {/* Admin tools - only for own profile and admin users */}
          {isOwnProfile && access.canAdmin() && (
            <div>
              <Text fw={500} size="sm" c="dimmed">
                Admin Tools
              </Text>
              <Group gap="xs" mt="xs">
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  loading={clearingCache}
                  onClick={async () => {
                    setClearingCache(true);
                    try {
                      await clearCache();
                      notifications.show({
                        title: 'Cache Cleared',
                        message: 'Server cache has been cleared successfully.',
                        color: 'green',
                      });
                    } catch (e: any) {
                      notifications.show({
                        title: 'Error',
                        message: e.message || 'Failed to clear cache',
                        color: 'red',
                      });
                    } finally {
                      setClearingCache(false);
                    }
                  }}
                >
                  Clear Server Cache
                </Button>
              </Group>
            </div>
          )}

          {/* Show contributions if verified or own profile */}
          {(profileUser.is_verified || isOwnProfile) && (
            <div>
              <Text fw={500} size="sm" c="dimmed">
                Contributions
              </Text>
              <Group gap="md" mt="xs">
                <Badge
                  variant="light"
                  color={
                    (profileUser.contribution_count || 0) > 10
                      ? 'green'
                      : (profileUser.contribution_count || 0) > 0
                        ? 'blue'
                        : 'gray'
                  }
                  size="lg"
                >
                  Total: {profileUser.contribution_count || 0}
                </Badge>
                <Badge
                  variant="light"
                  color={
                    (profileUser.last_30_days_contribution_count || 0) > 5
                      ? 'green'
                      : (profileUser.last_30_days_contribution_count || 0) > 0
                        ? 'blue'
                        : 'gray'
                  }
                  size="lg"
                >
                  Last 30 Days: {profileUser.last_30_days_contribution_count || 0}
                </Badge>
              </Group>
              {loadingBreakdown && (
                <Group gap="md" mt="xs">
                  <Loader size="sm" type="dots" />
                </Group>
              )}
              {!loadingBreakdown && contributionBreakdown && (
                <Group gap="md" mt="xs">
                  <Badge
                    variant="light"
                    color={contributionBreakdown.filmsCreated > 0 ? 'blue' : 'gray'}
                    size="lg"
                  >
                    Films Added: {contributionBreakdown.filmsCreated}
                  </Badge>
                  <Badge
                    variant="light"
                    color={contributionBreakdown.edits > 0 ? 'green' : 'gray'}
                    size="lg"
                  >
                    Edits: {contributionBreakdown.edits}
                  </Badge>
                </Group>
              )}
              <Text size="xs" c="dimmed" mt="xs">
                {profileUser.contribution_count === 0
                  ? isOwnProfile
                    ? 'Start contributing by suggesting films or edits to help build our database!'
                    : 'This user has not made any contributions yet.'
                  : profileUser.last_30_days_contribution_count === 0
                    ? isOwnProfile
                      ? "You haven't contributed this month. Consider suggesting some films or edits!"
                      : 'This user has not contributed this month.'
                    : isOwnProfile
                      ? 'Thank you for your contributions to the community!'
                      : 'Thank you for contributing to the community!'}
              </Text>
            </div>
          )}
        </Stack>
      </Card>

      <UserContributions userId={profileUser.id} />
    </div>
  );
};

export default ProfilePage;

export async function clientLoader({
  params,
}: Route.ClientLoaderArgs): Promise<{ profileUser: User }> {
  const { username } = params;

  if (!username) {
    throw new Response('Username is required', { status: 400 });
  }

  try {
    const profileUser = await getUserProfile(username);
    if (!profileUser) {
      throw redirect('/');
    }
    return { profileUser };
  } catch (error) {
    console.error('Error loading user profile:', error);
    // If it's already a Response, re-throw it
    if (error instanceof Response) {
      throw error;
    }
    // Check if it's a "User not found" error from the backend
    if (error instanceof Error && error.message.includes('not found')) {
      throw redirect('/');
    }
    // For other errors, redirect to homepage
    console.error('Unexpected error loading user profile:', error);
    throw redirect('/');
  }
}

export function meta({ loaderData: data }: Route.MetaArgs) {
  const username = data?.profileUser?.username || 'User';
  // Profiles render in the browser only: to a crawler every one is the same empty
  // page, and any /user/<anything> answers 200.
  return [
    { title: `${username}'s Profile - ${Settings.title}` },
    { name: 'robots', content: 'noindex' },
  ];
}
